import { useQuery } from '@tanstack/react-query';
import { USER_PREFERENCES_QUERY_KEY, fetchUserPreferences } from '../../services/preferencesService';
import { parameterLabel } from '../measurements/measurementHistoryUtils';

/** Cards of the dashboard without a choice of the user (and the parameters of the health score). */
export const DEFAULT_DASHBOARD_PARAMETERS: readonly string[] = ['alkalinity', 'nitrate', 'phosphate', 'calcium', 'magnesium', 'iodine'];

/** Cards a user can pin to the dashboard (backend: DASHBOARD_PARAMETERS_MAX). */
export const DASHBOARD_PARAMETERS_MAX = 12;

/** Chemical symbols stay as they are in every language; everything else is the translated name. */
const CARD_SYMBOLS: Readonly<Record<string, string>> = {
  alkalinity: 'KH',
  nitrate: 'NO₃',
  phosphate: 'PO₄',
  calcium: 'Ca',
  magnesium: 'Mg',
};

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/** Short label of a dashboard card ('KH', 'Iodine', 'Sr', …). */
export function dashboardCardLabel(t: TFunction, parameterId: string, name?: string): string {
  return Object.hasOwn(CARD_SYMBOLS, parameterId) ? (CARD_SYMBOLS[parameterId] as string) : parameterLabel(t, parameterId, name ?? parameterId);
}

/**
 * The cards the user picked in Settings → Dashboard, else the default set. While
 * the preferences load `isLoading` is true (the dashboard shows skeletons, not
 * the default cards first); if they cannot be loaded the default set is used.
 */
export function useDashboardParameters(): { parameters: readonly string[]; isLoading: boolean; isCustom: boolean } {
  const { data, isPending, isError } = useQuery({
    queryKey: USER_PREFERENCES_QUERY_KEY,
    queryFn: fetchUserPreferences,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const chosen = data?.dashboardParameters;
  return {
    parameters: chosen && chosen.length > 0 ? chosen : DEFAULT_DASHBOARD_PARAMETERS,
    isLoading: isPending && !isError,
    isCustom: Boolean(chosen && chosen.length > 0),
  };
}
